import {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
  MutableRefObject,
} from 'react'
import type { HotTable } from '@handsontable/react'
import { t } from '@lingui/core/macro'
import { Tooltip } from '@mui/material'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useHistory, useRouteMatch, useLocation, Prompt } from 'react-router-dom'
import { useUpdate } from 'react-use'
import { v4 as uuid } from 'uuid'
import { CvsButton, Loader, Paper } from '@components'
import { INNER_PAGE_HEIGHT } from '@components/layout'
import { templateContext } from '@contexts'
import { TableHeader } from '@datasets/components/table-header'
import { colors } from '@global/constants/colors'
import { tempIdGenerator } from '@utils/temp-id-generator'
import { api } from 'api'
import { showSnackbar, transpose2dArray, toDataRowsFromListColumns } from 'functions'
import { ListTableCore } from './list-table'
import {
  ListAttributesForm,
  TableReviewOverlap,
  TitleBlock,
  SuggestionsSidePanel,
} from './components'
import { SidePanelInterface } from './components/side-panel'
import { useLiveblocksHooks } from './useLiveblocksHooks'
import { ListType } from './types'
import type {
  List,
  ListApiResponse,
  ListResponse,
  EditListProps,
  TemplateResponse,
  DatasetQueryRequest,
  DatasetQueryResponse,
  TemplateField,
} from './types'

interface EditListRouterParams {
  id: string
}

export const EditList = (props: EditListProps) => {
  const { shouldPersist, afterSave } = props

  const match = useRouteMatch<EditListRouterParams>()
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const [list, setList] = useState<List>({
    id: null,
    name: '',
    powered_template_id: null,
    list_columns: [],
    type: 'drop-down',
  })
  const { byId: templatesById, fetchTemplate } = useContext(templateContext)
  const [listName, setListName] = useState('')
  const [listDescription, setListDescription] = useState('')
  const [listType, setListType] = useState<ListType>(ListType.DropDown)
  const listId = match.params.id
  const update = useUpdate()
  const [loading, setLoading] = useState(true)
  const [unsavedChanges, setUnsavedChanges] = useState(listId === 'new')
  const [searchTerm] = useState('')
  const [readOnly, setReadOnly] = useState(false)
  const [shouldRefetch, setShouldRefetch] = useState(false)

  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false)
  const sidePanelRef = useRef<SidePanelInterface>(null)

  const {
    isLiveblocksEnabled,
    isApproverMode,
    isOwner,
    isLocked,
    threads,
    activeCommentTarget,
    setActiveCommentTarget,
    hasPendingComments,
    checkPendingStatus,
    updateLocalThreadStatus,
    queueThreadResolution,
    handlePendingThreadResolutions,
    queuedResolutionsRef,
    createThread,
    editThreadMetadata,
    selectedCommentType,
    setSelectedCommentType,
    threadsToDisplayInSidePanel,
    hasUnresolvedGeneralComments,
  } = useLiveblocksHooks(props, { setReadOnly, setShouldRefetch })

  const handleDrawerOpen = () => {
    sidePanelRef.current?.handleToggleSidePanel()
  }

  const tableRef = useRef<HotTable | null>(null)

  const poweredByTemplate: { id: string; name: string } | null = useMemo(
    () =>
      list.powered_template_id
        ? {
            id: list.powered_template_id,
            name: templatesById[list.powered_template_id]?.name ?? '',
          }
        : null,
    [list.powered_template_id, templatesById],
  )

  useEffect(() => {
    if (listId !== 'new') {
      if (loading || shouldRefetch) {
        api
          .get<ListResponse>(`/api/v1/account/lists/${listId}`)
          .then(({ data }) => {
            const copy: List = {
              ...data,
              list_columns: data.list_columns.length
                ? data.list_columns
                : [{ id: tempIdGenerator('list'), name: '', precedence: 0, list_values: [] }],
            }
            copy.list_columns = copy.list_columns.map(lc =>
              lc.list_values.length
                ? { ...lc, list_values: lc.list_values }
                : {
                    ...lc,
                    list_values: [{ row_number: 0, id: tempIdGenerator('list'), value: null }],
                  },
            )
            setList(copy)
            setListName(copy.name ?? '')
            setListDescription(copy.notes ?? '')
            setListType(copy.type ?? ListType.DrillDown)
            if (copy.powered_template_id) {
              void fetchTemplate(copy.powered_template_id)
            }
            setLoading(false)
            setShouldRefetch(false)
          })
          .catch(() => {
            setLoading(false)
            setShouldRefetch(false)
          })
        return
      }
      return
    }

    if (location.search.includes('duplicate')) {
      const duplicateId = location.search.split('=')[1]
      api
        .get<ListResponse>(`/api/v1/account/lists/${String(duplicateId)}`)
        .then(({ data }) => {
          const { ...copy } = data
          const copyName = copy.name
          const newData: List = {
            ...copy,
            name: t`Duplicate of ${copyName}`,
            list_columns: copy.list_columns.map(({ id: _id, list_id: _list_id, ...rest }) => {
              const newListColumnId = `TEMP_${uuid()}`
              return {
                ...rest,
                id: newListColumnId,
                list_values: rest.list_values.map(values => ({
                  ...values,
                  list_column_id: newListColumnId,
                })),
              }
            }),
          }
          setList(newData)
          if (newData.powered_template_id) {
            void fetchTemplate(data.powered_template_id)
          }
          setListName(newData.name ?? '')
          setListDescription(newData.notes ?? '')
          setListType(newData.type ?? ListType.DrillDown)
        })
        .catch(() => {
          setShouldRefetch(false)
        })
        .finally(() => {
          setLoading(false)
        })
      return
    }

    const columnIndexLabel = 1
    setList({
      id: null,
      list_columns: [
        {
          id: tempIdGenerator('lc'),
          name: t`Column ${columnIndexLabel}`,
          precedence: 0,
          list_values: Array(10)
            .fill(0)
            .map((_, idx) => ({ id: tempIdGenerator('list'), row_number: idx, value: null })),
        },
      ],
    })
    setLoading(false)
  }, [listId, loading, shouldRefetch, location.search, fetchTemplate])

  const setTableRef = useCallback(
    (ref: MutableRefObject<HotTable>) => {
      tableRef.current = ref.current
      update()
    },
    [update],
  )

  const warn = useCallback(() => {
    if (!loading) {
      dispatch(
        showSnackbar({
          type: 'ERROR',
          message: t`Changing or removing list columns will generate new identifiers for each column. For this reason, any fields or template fields that are mapped to the existing list columns will need to be updated.`,
        }),
      )
    }
  }, [loading])

  useEffect(() => {
    if (list.transformedColumns && listId) {
      setUnsavedChanges(false)
    }
  }, [list])

  const onPoweredSave = async ({ template, column }: { template: string; column: string }) => {
    setLoading(true)
    try {
      if (!template) {
        setList(old => ({
          ...old,
          powered_template_id: null,
          list_columns: [
            {
              id: tempIdGenerator('lc'),
              name: '',
              powered_template_field_id: null,
              precedence: 0,
              list_values: [{ row_number: 0, value: null, id: tempIdGenerator('lv') }],
            },
          ],
        }))
        return
      }

      const { data } = await api.get<TemplateResponse>(`/api/v1/account/template/${template}`)
      const filteredData =
        data.template_fields?.filter(
          (templateField): templateField is Required<TemplateField> =>
            templateField.field?.governance_type !== 'cv_id',
        ) ?? []

      const selectedColumns = () => {
        const columns = filteredData.map(tf => [tf.id, tf.field.id])

        if (listType === 'drop-down') {
          const selected = columns.find(([c]) => c === column)?.[1] ?? '*'
          return [selected]
        }
        return columns.map(([, v]) => v)
      }

      const tableData = await api.post<DatasetQueryRequest, DatasetQueryResponse>(
        `/api/core/v1/datasets/${String(templatesById[template]?.rollup_dataset_id)}/query`,
        { limit: 100, commitRef: 'HEAD', distinct: true, select: selectedColumns() },
      )

      if (listType === 'drop-down') {
        const templateField = filteredData.find(tf => tf.id === column)?.field
        setList(old => ({
          ...old,
          powered_template_id: template,
          list_columns: [
            {
              id: old.list_columns[0]?.id ?? '',
              name: templateField?.name ?? '',
              precedence: 0,
              powered_template_field_id: column,
              list_values: tableData.data?.rows?.map((row, index) => ({
                id: tempIdGenerator('lv'),
                row_number: index,
                value: templateField?.id ? String(row.data[templateField.id]?.value ?? '') : '',
              })) ?? [{ id: tempIdGenerator('lv'), row_number: 1, value: '' }],
            },
          ],
        }))

        return
      }

      setList(old => ({
        ...old,
        powered_template_id: template,
        list_columns: filteredData.map((templateField, index) => ({
          id: old.list_columns[index]?.id ?? tempIdGenerator('lc'),
          name: templateField.field.name,
          powered_template_field_id: templateField.id,
          precedence: index,
          list_values: tableData.data?.rows?.map((row, idx) => ({
            id: tempIdGenerator('list'),
            row_number: idx,
            value: templateField.field.id
              ? String(row.data[templateField.field.id]?.value ?? '')
              : '',
          })) ?? [{ id: tempIdGenerator('list'), row_number: 1, value: '' }],
        })),
      }))
    } finally {
      setLoading(false)
      warn()
    }
  }

  const onPoweredTemplateSave = useCallback(() => {
    let list_columns = [...list.list_columns].map(lc => ({ ...lc, list_values: [] }))
    if (list.type === 'drop-down' && list.list_columns.length > 1 && list_columns[0]) {
      list_columns = [list_columns[0]]
    }
    setUnsavedChanges(false)

    const request = {
      id: listId === 'new' ? null : listId,
      name: listName,
      notes: listDescription,
      type: listType,
      powered_template_id: poweredByTemplate?.id,
      list_columns,
    }

    return api
      .put<typeof request, { data: ListApiResponse }>('/api/v1/account/lists/', request)
      .then(async response => {
        const { data } = response

        if (afterSave) {
          await afterSave(true)
        }

        dispatch(
          showSnackbar({
            message: t`List saved successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
        if (listId === 'new') {
          history.replace(`/admin/lists/edit/${data.id}`)
        }
      })
  }, [
    list,
    listId,
    listName,
    listDescription,
    listType,
    poweredByTemplate,
    handlePendingThreadResolutions,
  ])

  function updateListAndColumnsIds(createdList: ListApiResponse) {
    const updatedListCols = [...list.list_columns]
    for (const [i, listColumn] of updatedListCols.entries()) {
      if (!createdList.list_columns[i]) break

      listColumn.id = createdList.list_columns[i].id
    }

    return updatedListCols
  }

  const onSave = async () => {
    if (poweredByTemplate) {
      return onPoweredTemplateSave()
    }

    if (shouldPersist && !(await shouldPersist())) {
      return
    }

    const updatedData = toDataRowsFromListColumns(list.list_columns, true)
    const trimmed = updatedData.map(row => row.map(cell => cell?.trim() ?? ''))

    function updateOrCreateListColumns(
      column: string[],
      columnIndex: number,
      listColumns?: typeof list.list_columns,
    ) {
      const listValues = column.map((value, valueIndex) => {
        // Try to find existing list value to preserve its ID
        const existingValue = listColumns?.[columnIndex]?.list_values.find(
          v => v.row_number === valueIndex,
        )
        return {
          id: existingValue?.id ?? tempIdGenerator('lv'),
          row_number: valueIndex,
          value,
        }
      })
      const currentListColumns = listColumns ?? []
      const columnIndexLabel = columnIndex + 1
      const listColumn = currentListColumns.find(cColumn => cColumn.precedence === columnIndex)

      const name = listColumns?.[columnIndex]?.name ?? t`Column ${columnIndexLabel}`

      if (!listColumn) {
        return {
          list_values: listValues,
          name,
          precedence: columnIndex,
        }
      }

      listColumn.list_values = listValues
      listColumn.name = name
      listColumn.precedence = columnIndex

      return listColumn
    }

    const saveList = {
      id: listId === 'new' ? null : listId,
      name: listName,
      notes: listDescription,
      type: listType,
      list_columns: transpose2dArray(trimmed).map((item, index) =>
        updateOrCreateListColumns(item, index, list.list_columns),
      ),
    }

    // if it's a dropdown remove any lingering other columns
    if (saveList.type === 'drop-down' && saveList.list_columns.length > 1) {
      saveList.list_columns = saveList.list_columns.splice(0, 1)
    }
    setUnsavedChanges(false)

    const response = await api.put<typeof saveList, { data: ListApiResponse }>(
      '/api/v1/account/lists/',
      saveList,
    )

    const data = response.data
    const updatedListCols = updateListAndColumnsIds(data)
    // Spread saveList to avoid losing other properties, since data does not provide it all.
    setList({
      ...saveList,
      id: data.id,
      list_columns: updatedListCols,
      updated_at: data.updated_at,
    })

    if (afterSave) {
      await afterSave(false)
    }

    dispatch(
      showSnackbar({
        message: t`List saved successfully`,
        type: 'SUCCESS',
        duration: 5000,
      }),
    )
    if (listId === 'new') {
      history.replace(`/admin/lists/edit/${data.id}`)
    }
  }

  const lastSavedText = (
    <span>
      {t`Last saved:`}
      <span
        style={{
          display: 'inline-block',
          background: unsavedChanges ? colors.yellow.main : colors.green.main,
          height: 8,
          width: 8,
          borderRadius: 4,
          margin: '0 4px',
        }}
      />
      {list.updated_at ? moment.utc(list.updated_at).local().format('MMM D, YYYY h:mma') : t`Never`}
    </span>
  )

  return (
    <motion.div
      className="edit-list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ height: '100%' }}
    >
      <Paper
        elevation={3}
        style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}
        data-test-page-id="edit-list"
      >
        <div style={{ display: 'flex', flexShrink: 1, borderBottom: '1px solid #CCC' }}>
          <TitleBlock
            list={list}
            isApproverMode={isApproverMode}
            isOwner={isOwner}
            isLocked={isLocked}
          />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: 24 }}>
              <ListAttributesForm
                list={list}
                isApproverMode={isApproverMode}
                state={{
                  name: listName,
                  description: listDescription,
                  type: listType,
                }}
                onPoweredSave={onPoweredSave}
                onChange={(attribute, value) => {
                  if (attribute === 'name') {
                    setListName(value)
                  }

                  if (attribute === 'description') {
                    setListDescription(value)
                  }

                  if (attribute === 'type') {
                    setListType(value as ListType)
                  }
                }}
              />
              {isApproverMode && (
                <div style={{ flexShrink: 1, whiteSpace: 'nowrap' }}>
                  <Tooltip
                    title={
                      hasPendingComments
                        ? t`Accept or reject all pending suggestions before saving`
                        : ''
                    }
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          textAlign: 'center',
                        },
                      },
                    }}
                  >
                    <span>
                      <CvsButton
                        size="large"
                        variant="contained"
                        color="secondary"
                        onClick={onSave}
                        disabled={!listName || hasPendingComments}
                        style={{ marginRight: 8 }}
                        data-test-button-id="save"
                      >
                        {t`Save`}
                      </CvsButton>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
            <div style={{ flexShrink: 1, height: 40 }}>
              <TableHeader
                setReadOnlyCallback={setReadOnly}
                template={list}
                lastSavedText={lastSavedText}
                type="list"
                poweredBy={poweredByTemplate}
                tableRef={tableRef.current?.hotInstance}
                noBadge={true}
                isApproverMode={isApproverMode}
                handleCommentsButton={handleDrawerOpen}
                isSidePanelOpen={isSidePanelOpen}
              />
            </div>
          </div>
        </div>
        <div
          id="HOTContainer"
          style={{
            width: '100%',
            height: INNER_PAGE_HEIGHT,
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
          className="submission-table list-table"
        >
          <div
            id="list-container"
            className="handsontable-container"
            style={{ height: '100%', position: 'relative' }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                ...(isLocked && { pointerEvents: 'none' }),
              }}
              id="table-wrapper"
            >
              {loading ? (
                <Loader />
              ) : (
                <ListTableCore
                  readOnly={readOnly}
                  searchTerm={searchTerm}
                  tableRef={tableRef}
                  setTableRef={setTableRef}
                  list={list}
                  setList={setList}
                  type={listType}
                  onChange={() => {
                    setUnsavedChanges(true)
                  }}
                  poweredByTemplate={poweredByTemplate}
                  threads={threads}
                  activeCommentTarget={activeCommentTarget}
                  setActiveCommentTarget={setActiveCommentTarget}
                  checkPendingStatus={checkPendingStatus}
                  updateLocalThreadStatus={updateLocalThreadStatus}
                  queueThreadResolution={queueThreadResolution}
                  queuedResolutionsRef={queuedResolutionsRef}
                  createThread={createThread}
                  editThreadMetadata={editThreadMetadata}
                  isApproverMode={isApproverMode}
                />
              )}
            </div>

            {isLocked && <TableReviewOverlap />}
          </div>
          {/* Custom Drawer */}
          {isLiveblocksEnabled && (
            <SuggestionsSidePanel
              list={list}
              sidePanelRef={sidePanelRef}
              onToggle={setIsSidePanelOpen}
              tableRef={tableRef}
              selectedCommentType={selectedCommentType}
              setSelectedCommentType={setSelectedCommentType}
              threadsToDisplayInSidePanel={threadsToDisplayInSidePanel}
              hasUnresolvedGeneralComments={hasUnresolvedGeneralComments}
              updateLocalThreadStatus={updateLocalThreadStatus}
              queueThreadResolution={queueThreadResolution}
              isApproverMode={isApproverMode}
              isLocked={isLocked}
            />
          )}
        </div>
      </Paper>
      <Prompt
        when={unsavedChanges}
        message={t`You are about to lose any unsaved changes by leaving this page. Are you sure you want to continue?`}
      />
    </motion.div>
  )
}
